/**
 * PaginationRenderer — extracted from DynamicTableV2.
 * Renders pagination footer, load-more button, or infinite scroll indicator
 * depending on the pagination mode.
 */

import React from 'react';
import { Pagination } from '@packages/ui/patterns/Pagination';
import { Button } from '@packages/ui/primitives/Button';
import { Spinner } from '@packages/ui/primitives/Spinner';
import { TABLE_IDS } from '../semantic-contract';

export interface PaginationRendererProps {
  mode: 'pagination' | 'loadMore' | 'infiniteScroll' | 'clientSide' | 'none';
  hasData: boolean;

  pageIndex: number;
  pageSize: number;
  totalCount: number;
  /** Rows on the current page — drives the "X of Y items" label in DS Pagination. */
  visibleCount?: number;
  onPageChange: (pageIndex: number) => void;
  onPageSizeChange: (pageSize: number) => void;

  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
}

export function PaginationRenderer({
  mode,
  hasData,
  pageIndex,
  pageSize,
  totalCount,
  visibleCount,
  onPageChange,
  onPageSizeChange,
  onLoadMore,
  hasMore,
  isLoadingMore,
}: PaginationRendererProps): React.ReactElement | null {
  if (mode === 'none' || !hasData) return null;

  if (mode === 'loadMore') {
    if (!onLoadMore || !hasMore) return null;
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          padding: '16px 0',
          position: 'sticky',
          left: 0,
        }}
        data-testid='dt-v2-load-more-container'>
        <Button
          semanticId={TABLE_IDS.loadMoreButton}
          label='Load More'
          type='button'
          variant='secondary'
          onClick={onLoadMore}
          loading={isLoadingMore}
          disabled={isLoadingMore}
          data-testid='dt-v2-load-more-button'
        />
      </div>
    );
  }

  if (mode === 'infiniteScroll') {
    if (!hasMore) return null;
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '16px 0',
          position: 'sticky',
          left: 0,
          minHeight: 48,
        }}
        data-testid='dt-v2-infinite-scroll-indicator'>
        {isLoadingMore && <Spinner size='sm' semanticId='infiniteScrollSpinner' />}
      </div>
    );
  }

  if (mode === 'pagination' || mode === 'clientSide') {
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    const resolvedVisibleCount =
      visibleCount ?? Math.min(pageSize, Math.max(0, totalCount - pageIndex * pageSize));
    return (
      <Pagination
        totalCount={totalCount}
        visibleCount={resolvedVisibleCount}
        currentPage={pageIndex + 1}
        pageSize={pageSize}
        totalPages={totalPages}
        showPageSize
        onPageChange={(page: number) => onPageChange(page - 1)}
        onPageSizeChange={onPageSizeChange}
        semanticId={TABLE_IDS.pagination}
      />
    );
  }

  return null;
}
